import {Component} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DashboardSection, FraudReportType} from '@npck/af-frontend-api';
import {RouterService, TAB} from '@npck/libs/commons';
import {DASHBOARD_SECTION, FRAUD_REPORT_TYPE} from '../../domain/query-params.keys';
import {DialogService} from '@npck/libs/ui-kit';
import {Observable, Subject} from 'rxjs';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {Path} from '../../path';
import {PanelViewModel} from './action-pannel/action-panel.component';
import {sectionToSlug} from './dashboard-section';
import {CreateTrendDialogComponent} from './trends/create-edit/start-generate-trend-dialog/create-trend-dialog.component';

const INCIDENT_PANELS: IncidentPanel[] = [
    {key: 'Fraud', icon: 'ic_fraud', section: DashboardSection.INCIDENT_FRAUD},
    {key: 'Drug', icon: 'ic_drug', section: DashboardSection.INCIDENT_DRUG},
    {key: 'PyramidScheme', icon: 'ic_pyramid_scheme', section: DashboardSection.INCIDENT_PYRAMID_SCHEME},
    {key: 'Casino', icon: 'ic_casino', section: DashboardSection.INCIDENT_CASINO},
];

const FRAUD_REPORT_PANELS: FraudReportPanel[] = [
    {key: 'NBRK_PAYMENT_FRAUD', icon: 'ic_document', reportType: FraudReportType.NBRK_PAYMENT_FRAUD},
    {key: 'ARDFM_LOAN_FRAUD', icon: 'ic_document', reportType: FraudReportType.ARDFM_LOAN_FRAUD},
    {key: 'CPSA_PAYMENT_FRAUD', icon: 'ic_document', reportType: FraudReportType.CPSA_PAYMENT_FRAUD},
    {key: 'CPSA_LOAN_FRAUD', icon: 'ic_document', reportType: FraudReportType.CPSA_LOAN_FRAUD},
];

export const ANALYTIC_TREND_TAB = 'tabTrends';
export const ANALYTIC_FRAUD_REPORTS_TAB = 'tabFraudReports';

@Component({
    selector: 'af-analytics',
    standalone: false,
    templateUrl: './analytics.component.html',
    styleUrl: './analytics.component.scss',
})
export class AnalyticsComponent {
    tabs: AnalyticsTab[] = [
        'tabIncidents',
        'tabUsers',
        'tabRelations',
        'tabClassification',
        ANALYTIC_TREND_TAB,
        ANALYTIC_FRAUD_REPORTS_TAB
    ];
    activeTab: AnalyticsTab;
    selectedTabIndex: number;
    showTrendDownloadReportButton: boolean = false;
    showRunNewTrendAnalysisButton: boolean = false;
    disabledTabs: AnalyticsTab[] = ['tabUsers', 'tabRelations'];
    tabsViewModel: Record<AnalyticsTab, PanelViewModel[]> = this.createViewModel();
    buttonClickSubject = new Subject<AnalyticsTab>();

    constructor(
        private routerService: RouterService,
        private activatedRoute: ActivatedRoute,
        private authHolderService: AuthHolderService,
        private dialogService: DialogService,
    ) {
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as AnalyticsTab || 'tabIncidents';
        this.selectedTabIndex = this.tabs.findIndex(tab => tab == this.activeTab);
        if (!this.authHolderService.hasAccess(['npck-admin', 'organization-supervisor'])) {
            this.disabledTabs.push(ANALYTIC_FRAUD_REPORTS_TAB);
        }
        this.initTrendButtons();
    }

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        this.routerService.navigate(
            Path.ANALYTICS,
            {
                queryParams: {[TAB]: this.activeTab},
                queryParamsHandling: 'merge',
            });
        this.initTrendButtons();
    }

    generateNewTrendAnalysis = (): Observable<void> => {
        return this.dialogService.openCustomDialog<CreateTrendDialogComponent, null>(
            CreateTrendDialogComponent,
            {}, {disableClose: true}
        );
    };

    private initTrendButtons(): void {
        if (!this.authHolderService.hasAccess(['npck-admin', 'finance-user'])) {
            this.disabledTabs.push(ANALYTIC_TREND_TAB);
            this.showRunNewTrendAnalysisButton = false;
            this.showTrendDownloadReportButton = false;
            return;
        }

        const isTrendTabActive = this.activeTab === ANALYTIC_TREND_TAB;

        this.showTrendDownloadReportButton = isTrendTabActive;
        this.showRunNewTrendAnalysisButton = this.authHolderService.hasAccess(['npck-admin'])
            && isTrendTabActive;
    }

    private createViewModel(): Record<AnalyticsTab, PanelViewModel[]> {
        return this.tabs.reduce((acc, tab) => {
            acc[tab] = this.createPanels(tab);
            return acc;
        }, {} as Record<AnalyticsTab, PanelViewModel[]>);
    }

    private createPanels(tab: AnalyticsTab): PanelViewModel[] {
        switch (tab) {
            case 'tabClassification':
            case 'tabIncidents':
                return this.createIncidentTabPanels(tab);
            case 'tabUsers':
                return [];
            case 'tabRelations':
                return [];
            case ANALYTIC_TREND_TAB:
                return [];
            case ANALYTIC_FRAUD_REPORTS_TAB:
                return this.createFraudReportTabPanels();
        }
    }

    private createIncidentTabPanels(tab: AnalyticsTab): PanelViewModel[] {
        const isIncidents = tab === 'tabIncidents';
        return INCIDENT_PANELS.map(panel => ({
            title: `i18n_af_panelTitle#${panel.key}`,
            subtitle: `i18n_af_panelSubtitle#${panel.key}${isIncidents ? 'Incidents' : 'Classification'}`,
            icon: panel.icon,
            disabled: !isIncidents && panel.section !== DashboardSection.INCIDENT_FRAUD,
            click: () => this.navigateToPanel(isIncidents, panel.section),
        }));
    }

    private createFraudReportTabPanels(): PanelViewModel[] {
        return FRAUD_REPORT_PANELS.map(panel => ({
            title: `i18n_af_panelTitle#${panel.key}`,
            subtitle: `i18n_af_panelSubtitle#${panel.key}`,
            badgeTitle: `i18n_af_panelBadgeTitle#${panel.key}`,
            icon: panel.icon,
            disabled: panel.reportType !== FraudReportType.NBRK_PAYMENT_FRAUD,
            click: () => this.navigateToFraudReportPanel(panel.reportType),
        }));
    }

    private navigateToPanel(isIncidents: boolean, section: DashboardSection): void {
        const path = isIncidents
            ? Path.ANALYTICS_INCIDENTS_DASHBOARDS.replace(`:${DASHBOARD_SECTION}`, sectionToSlug(section))
            : Path.ANALYTICS_CLASSIFICATIONS_FRAUD;
        this.routerService.navigate(path, {queryParams: {[TAB]: this.activeTab}});
    }

    private navigateToFraudReportPanel(fraudReportType: FraudReportType): void {
        const path = Path.ANALYTICS_FRAUD_REPORTS.replace(`:${FRAUD_REPORT_TYPE}`, fraudReportType);
        this.routerService.navigate(path, {queryParams: {[TAB]: this.activeTab}});
    }
}

interface IncidentPanel {
    key: string;
    icon: string;
    section: DashboardSection;
}

interface FraudReportPanel {
    key: string;
    icon: string;
    reportType: FraudReportType;
}

export declare type AnalyticsTab = 'tabIncidents' | 'tabUsers' | 'tabRelations' | 'tabClassification' | 'tabTrends' | 'tabFraudReports';
